import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'src/store/store';

import { fetchCurrentCommunity } from 'src/modules/shared/context/actions';
import {
  fetchArchive,
  fetchCommunityTopTags,
  fetchPinnedPosts,
  fetchPosts,
  fetchTotalMonthlyPosts,
  fetchLatestPoll,
} from 'src/modules/community/communityPosts/actions';

import { fetchCommunityTopics } from 'src/modules/community/shared/actions';

import { getCommunityPostsSeo } from 'src/utils/seo';
import postsFilterOptions from 'src/constants/postsFilterOptions';

import WithErrorHandling, { RequestError } from 'src/components/hoc/withErrorHandling';
import { CommunityLayout } from 'src/components/layouts';
import CommunityPostsContent from 'src/modules/community/communityPosts/components/communityPostsContent';
import CommunityPostsSidebar from 'src/modules/community/communityPosts/components/communityPostsSidebar';
import { PageContext } from 'src/constants/types';
import { GoogleAds } from 'src/modules/shared/modal/components/googleAdsModal';

function CommunityPostsPage() {
  const dispatch = useDispatch();
  const { asPath } = useRouter();

  const { currentCommunity } = useSelector(({ context }) => context);
  const {
    archive = {},
    currentPageNumber,
    selectedTopic = '',
    selectedTopicId,
    topTags = [],
  } = useSelector(({ pages }) => pages.communityPosts);

  const communitySlug = currentCommunity.slug;
  const isPollsList = asPath.endsWith('polls');

  useEffect(() => {
    if (isPollsList) {
      dispatch(fetchPosts({ communitySlug, filter: 'polls' }));
    } else {
      // Loading the page with a fragment in the URL (popular, pinned, etc)
      const filter = asPath.split('#')[1];
      const isFilterOption = postsFilterOptions.find(({ value }) => value === filter);

      // Prevent fetching again if hash is 'latest' as it is the default filter
      if (isFilterOption) {
        dispatch(fetchPosts({
          communitySlug,
          filter,
          topicId: selectedTopicId,
          topicName: selectedTopic,
        }));
      }
    }
  }, [isPollsList, asPath, communitySlug, dispatch, selectedTopic, selectedTopicId]);

  useEffect(() => {
    // When landing on the page from the community navigation
    // Scroll to the navigation bar on mobile only
    const isMobile = document.documentElement.clientWidth < 768;
    const hash = location?.hash?.replace('#', '');
    if (isMobile && hash === 'mobile') {
      const position = document.getElementById('js-community-navbar').offsetTop;
      window.scrollTo(0, position - 52);
    }
  }, []);

  return (
    <CommunityLayout
      currentPage="posts"
      seo={getCommunityPostsSeo(currentCommunity, topTags, archive.currentYear, archive.currentMonth, currentPageNumber, isPollsList)}
      sideBarContent={<CommunityPostsSidebar />}
    >
      <CommunityPostsContent />
      <GoogleAds pageType="posts" />
    </CommunityLayout>
  );
}

CommunityPostsPage.getInitialProps = async ({
  store: { dispatch, getState },
  query: { communitySlug, topicName, year, month = '01', page },
  asPath,
}: PageContext) => {

  await dispatch(fetchCurrentCommunity(communitySlug));

  const state = getState();
  const { currentCommunity, communityErrorStatusCode, lang, currentUser } = state.context;

  if (!currentCommunity) {
    const isHiddenPrivateCommunity = communityErrorStatusCode === 310;
    if (isHiddenPrivateCommunity) {
      if (!currentUser) throw new RequestError('This page is not available to visitors', 401);
      throw new RequestError('This is a private community', 404); // user is not a member
    }
    throw new RequestError('Error fetching community', communityErrorStatusCode);
  }

  await dispatch(fetchCommunityTopics(communitySlug)); // topics have to be fetched after current community is in redux

  const { topics } = getState().context.currentCommunity;
  const filter = asPath.endsWith('polls') ? 'polls' : 'latest';
  const topic = topics.find(({ name }) => name.toLowerCase() === decodeURIComponent(topicName));

  await Promise.all([
    dispatch(fetchPosts({ communitySlug, filter, topicId: topic?.id, topicName: topic?.name, year, month, pageNumber: Number(page) })),
    dispatch(fetchCommunityTopTags(currentCommunity.communityId, { limit: 3, language: lang })),
    dispatch(fetchPinnedPosts(communitySlug)),
    dispatch(fetchLatestPoll(communitySlug)),
    !currentCommunity.isPrivate && dispatch(fetchArchive(communitySlug)),
    year && dispatch(fetchTotalMonthlyPosts(communitySlug, year, month)),
  ]);

  return {};
};

export default WithErrorHandling(CommunityPostsPage);
